/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import https from 'https';
import getConfig from '../../config';

class GeoService {
	/**
	 * Retrieves the location data from Geo Service.
	 * @param  {boolean} noCache, Indicates if a new request should be made to fetch data
	 */
	fetchGeoData(noCache?: boolean) {
		return this._performGeoRequest(noCache);
	}

	/*
	 **
	 **  P R I V A T E   M E T H O D S
	 **
	 */

	_requestPromise: Promise<any>;

	/**
	 * Retrieves geoData from GeoService.
	 * @returns {Object}
	 */
	_performGeoRequest(noCache?: boolean) {
		if (!noCache && this._requestPromise) {
			return this._requestPromise;
		}
		this._requestPromise = new Promise((resolve, reject) => {
			const options = {
				'Content-Type': 'application/json',
				Accept: 'application/json',
				hostname: getConfig('ADOBE_DOT_COM_GEO_URL'),
				method: 'GET',
			};
			const request = https.request(options, (res) => {
				if (res.statusCode && res.statusCode >= 300) {
					// On error, it should show the error status code
					window.cchome.util.logger.error('Geo Service', 'Problem with Geo Service response', res);
					const geoServiceError = new Error('_performGeoRequest error');
					reject(geoServiceError);
				}
				else {
					let data = '';
					res.setEncoding('utf8');
					res.on('data', chunk => (data += chunk));
					res.on('end', () => {
						// Success!
						try {
							resolve(JSON.parse(data));
						}
						catch (e: any) {
							window.cchome.util.logger.error(
								'Geo Service',
								'Problem with Geo Service response data',
								e.toString(),
							);
							const geoServiceError = new Error('_performGeoRequest error');
							reject(geoServiceError);
						}
					});
				}
			});

			request.on('error', (err) => {
				window.cchome.util.logger.error('Geo Service', 'Problem with Geo Service request', err.toString());
				const geoServiceError = new Error('_performGeoRequest error');
				reject(geoServiceError);
			});
			request.end();
		});
		return this._requestPromise;
	}
}

export default GeoService;
